import { LOGS } from "./categories";
import { createConsoleSink } from "./sinks/consoleSink";
import {
    LogCategories,
    LogEntry,
    LogLevel,
    LogSink,
    SinkPolicy,
} from "./types";

const sinks: LogSink[] = [];

export const registerLogSink = (sink: LogSink): (() => void) => {
    const existing = sinks.findIndex(
        (candidate) => candidate.name === sink.name,
    );
    if (existing >= 0) {
        sinks.splice(existing, 1, sink);
    } else {
        sinks.push(sink);
    }

    return () => unregisterLogSink(sink.name);
};

export const unregisterLogSink = (name: string): void => {
    const index = sinks.findIndex((candidate) => candidate.name === name);
    if (index >= 0) {
        sinks.splice(index, 1);
    }
};

export const getLogSink = (name: string): LogSink | undefined =>
    sinks.find((candidate) => candidate.name === name);

export const clearLogSinks = (): void => {
    sinks.length = 0;
};

export const setSinkPolicy = (
    name: string,
    policy: Partial<SinkPolicy>,
): void => {
    getLogSink(name)?.setPolicy?.(policy);
};

export const getSinkPolicy = (name: string): SinkPolicy | undefined =>
    getLogSink(name)?.getPolicy?.();

const toArray = (categories: LogCategories): LOGS[] =>
    Array.isArray(categories) ? categories : [categories];

const emit = (
    level: LogLevel,
    categories: LogCategories,
    message: string,
    args: unknown[],
    error?: Error,
): void => {
    if (sinks.length === 0) {
        return;
    }

    const entry: LogEntry = {
        level,
        categories: toArray(categories),
        message,
        args,
        error,
        timestamp: Date.now(),
    };

    for (const sink of sinks) {
        try {
            sink.handle(entry);
        } catch {
            // Un sink défaillant ne doit pas casser l'appelant.
        }
    }
};

export const now = (): number =>
    typeof performance !== "undefined" && typeof performance.now === "function"
        ? performance.now()
        : Date.now();

export const logger = {
    info: (categories: LogCategories, message: string, ...args: unknown[]) =>
        emit("info", categories, message, args),

    warn: (categories: LogCategories, message: string, ...args: unknown[]) =>
        emit("warn", categories, message, args),

    error: (
        categories: LogCategories,
        message: string,
        cause?: unknown,
        ...args: unknown[]
    ) => {
        if (cause instanceof Error) {
            emit("error", categories, message, args, cause);
            return;
        }

        emit(
            "error",
            categories,
            message,
            cause === undefined ? args : [cause, ...args],
        );
    },

    perf: (
        categories: LogCategories,
        message: string,
        startTime: number,
        ...args: unknown[]
    ) => {
        const elapsed = (now() - startTime).toFixed(2);
        emit("info", categories, `${message} (${elapsed}ms)`, args);
    },
};

registerLogSink(createConsoleSink());
